import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar, Badge, Spin, Divider } from 'antd';
import { useNavigate, Outlet } from 'react-router-dom';
import { 
  UserOutlined, 
  BellOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import axios from 'axios';

const { Header, Content, Footer, Sider } = Layout;

const MainLayout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ✅ Notification state'leri
  const [notifications, setNotifications] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('urlToken');
  const user = JSON.parse(localStorage.getItem('user')) || { supplierName: 'Guest', email: '' };

  // ✅ Notification fetch
  useEffect(() => {
    if (user?.role === 'Supplier' && token) {
      fetchNotifications();
      
      const interval = setInterval(() => {
        fetchNotifications();
      }, 5 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [user?.role, token]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/SupplierNotifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setNotifications(response.data.notifications || []);
      setNotificationSummary({
        count: response.data.count,
        critical: response.data.critical,
        warning: response.data.warning,
        info: response.data.info,
      });
    } catch (error) {
      console.error('Notification fetch error:', error);
      setNotifications([]);
      setNotificationSummary({ count: 0, critical: 0, warning: 0, info: 0 });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Notification icon - Kompakt boyutlar
  const getNotificationIcon = (type, category) => {
    const baseStyle = { 
      fontSize: '14px',
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      flexShrink: 0,
    };
    
    switch (type) {
      case 'error':
        return (
          <div style={{ ...baseStyle, backgroundColor: '#fff1f0', color: '#ff4d4f' }}>
            <ExclamationCircleOutlined />
          </div>
        );
      case 'warning':
        return (
          <div style={{ ...baseStyle, backgroundColor: '#fffbe6', color: '#faad14' }}>
            <ClockCircleOutlined />
          </div>
        );
      case 'info':
        return (
          <div style={{ ...baseStyle, backgroundColor: '#e6f7ff', color: '#1890ff' }}>
            <InfoCircleOutlined />
          </div>
        );
      default:
        return (
          <div style={{ ...baseStyle, backgroundColor: '#f6ffed', color: '#52c41a' }}>
            <CheckCircleOutlined />
          </div>
        );
    }
  };

  // ✅ Category badge - Daha kompakt
  const getCategoryBadge = (category) => {
    const badgeStyle = {
      fontSize: '9px',
      padding: '1px 5px',
      borderRadius: '3px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
    };

    if (category?.includes('certificate')) {
      return (
        <span style={{ ...badgeStyle, backgroundColor: '#e6f7ff', color: '#1890ff' }}>
          <FileTextOutlined style={{ fontSize: '9px' }} />
          Cert
        </span>
      );
    }
    if (category?.includes('audit')) {
      return (
        <span style={{ ...badgeStyle, backgroundColor: '#fff7e6', color: '#fa8c16' }}>
          <CalendarOutlined style={{ fontSize: '9px' }} />
          Audit
        </span>
      );
    }
    return (
      <span style={{ ...badgeStyle, backgroundColor: '#f0f0f0', color: '#595959' }}>
        <InfoCircleOutlined style={{ fontSize: '9px' }} />
        Info
      </span>
    );
  };

  // ✅ Notification dropdown menu - Kompakt boyutlar
  const notificationMenu = {
    items: notifications.length > 0 ? [
      {
        key: 'header',
        label: (
          <div style={{ 
            padding: '10px 12px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: '#fafafa',
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: '700',
                color: '#262626',
              }}>
                {t('notifications')}
              </span>
              <Badge 
                count={notificationSummary?.count || 0} 
                style={{ 
                  backgroundColor: '#1890ff',
                  fontWeight: '600',
                  fontSize: '11px',
                  height: '18px',
                  lineHeight: '18px',
                  minWidth: '18px',
                }}
              />
            </div>
            
            {notificationSummary && (notificationSummary.critical > 0 || notificationSummary.warning > 0) && (
              <div style={{ 
                display: 'flex', 
                gap: '6px',
              }}>
                {notificationSummary.critical > 0 && (
                  <div style={{ 
                    flex: 1,
                    padding: '4px 6px',
                    backgroundColor: '#fff1f0',
                    borderRadius: '4px',
                    border: '1px solid #ffccc7',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '11px' }} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#ff4d4f' }}>
                      {notificationSummary.critical}
                    </span>
                  </div>
                )}
                {notificationSummary.warning > 0 && (
                  <div style={{ 
                    flex: 1,
                    padding: '4px 6px',
                    backgroundColor: '#fffbe6',
                    borderRadius: '4px',
                    border: '1px solid #ffe58f',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <WarningOutlined style={{ color: '#faad14', fontSize: '11px' }} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#faad14' }}>
                      {notificationSummary.warning}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ),
        disabled: true,
      },
      ...notifications.slice(0, 4).map((notif, index) => ({
        key: index,
        label: (
          <div style={{ 
            padding: '10px 12px',
            borderBottom: index < Math.min(notifications.length - 1, 3) ? '1px solid #f5f5f5' : 'none',
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              {/* Icon */}
              {getNotificationIcon(notif.type, notif.category)}
              
              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Category Badge */}
                <div style={{ marginBottom: '4px' }}>
                  {getCategoryBadge(notif.category)}
                </div>
                
                {/* Title */}
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '12px', 
                  marginBottom: '3px',
                  color: '#262626',
                  lineHeight: '1.3',
                }}>
                  {notif.title}
                </div>
                
                {/* Message */}
                <div style={{ 
                  fontSize: '11px', 
                  color: '#595959',
                  lineHeight: '1.4',
                  marginBottom: '4px',
                }}>
                  {notif.message}
                </div>
                
                {/* Time indicators */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                  {notif.daysRemaining !== undefined && (
                    <span style={{ 
                      fontSize: '10px',
                      padding: '1px 6px',
                      backgroundColor: '#e6f7ff',
                      color: '#1890ff',
                      borderRadius: '8px',
                      fontWeight: '500',
                    }}>
                      <ClockCircleOutlined style={{ marginRight: '3px', fontSize: '9px' }} />
                      {notif.daysRemaining}d
                    </span>
                  )}
                  
                  {(notif.daysExpired !== undefined || notif.daysOverdue !== undefined) && (
                    <span style={{ 
                      fontSize: '10px',
                      padding: '1px 6px',
                      backgroundColor: '#fff1f0',
                      color: '#ff4d4f',
                      borderRadius: '8px',
                      fontWeight: '600',
                    }}>
                      <ExclamationCircleOutlined style={{ marginRight: '3px', fontSize: '9px' }} />
                      {notif.daysExpired || notif.daysOverdue}d
                    </span>
                  )}
                </div>

                {/* Details list - Sadece ilk 2 item */}
                {notif.details && Array.isArray(notif.details) && (
                  <div style={{ 
                    marginTop: '6px',
                    padding: '5px 6px',
                    backgroundColor: '#fafafa',
                    borderRadius: '3px',
                    borderLeft: '2px solid #d9d9d9',
                  }}>
                    {notif.details.slice(0, 2).map((detail, i) => (
                      <div key={i} style={{ 
                        fontSize: '10px', 
                        color: '#595959',
                        marginBottom: i < Math.min(notif.details.length - 1, 1) ? '3px' : '0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        <span style={{ 
                          width: '3px', 
                          height: '3px', 
                          backgroundColor: '#8c8c8c',
                          borderRadius: '50%',
                          flexShrink: 0,
                        }} />
                        {detail}
                      </div>
                    ))}
                    {notif.details.length > 2 && (
                      <div style={{ fontSize: '10px', color: '#8c8c8c', marginTop: '3px' }}>
                        +{notif.details.length - 2} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      })),
      {
        key: 'divider',
        label: <Divider style={{ margin: 0 }} />,
        disabled: true,
      },
      {
        key: 'view-all',
        label: (
          <div 
            onClick={() => navigate('/main/customer')}
            style={{ 
              padding: '10px 12px',
              textAlign: 'center',
              color: '#1890ff',
              fontWeight: '600',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e6f7ff';
              e.currentTarget.style.color = '#0050b3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#1890ff';
            }}
          >
            {t('view_all_notifications')}
            <RightOutlined style={{ fontSize: '10px' }} />
          </div>
        ),
      },
    ] : [
      {
        key: 'empty',
        label: (
          <div style={{ 
            padding: '40px 30px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              backgroundColor: '#f6ffed',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <CheckCircleOutlined style={{ 
                fontSize: '24px',
                color: '#52c41a',
              }} />
            </div>
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '600',
              color: '#262626',
              marginBottom: '4px',
            }}>
              {t('no_notifications')}
            </div>
            <div style={{ 
              fontSize: '11px',
              color: '#8c8c8c',
            }}>
              {t('all_up_to_date')}
            </div>
          </div>
        ),
        disabled: true,
      },
    ],
  };

  const totalCount = (notificationSummary?.critical || 0) + 
                    (notificationSummary?.warning || 0) + 
                    (notificationSummary?.info || 0);

  // Menü öğesi tıklama işlevi
  const onClick = (e) => {
    console.log('click ', e);

    switch (e.key) {
      case '13':
        navigate('/main/customer');
        break;
      case '14':
        navigate('/main/certificate-upload');
        break;
      case '18':
        navigate('/main/supplier-performance');
        break;
      case '19':
        navigate('/main/supplier-contacts');
        break;
      default:
        break;
    }
  };

  // Menü öğeleri
  const items = [
    {
      key: 'grp',
      label: t("sidebar_supplier_portal"),
      type: 'group',
      children: [
        {
          key: '13',
          label: t("sidebar_supplier_dashboard"),
        },
        {
          key: '14',
          label: t("sidebar_certificate_upload"),
        },
        {
          key: '18',
          label: t("sidebar_supplier_performance"),
        },
        {
          key: '19',
          label: t("sidebar_supplier_contacts"),
        },
      ],
    },
  ];

  // User Dropdown Menü
  const userMenu = (
    <Menu
      items={[
        {
          key: '1',
          label: (
            <div>
              <strong>{user.supplierName}</strong>
              <br />
              <span style={{ fontSize: '12px', color: 'gray' }}>{user.email}</span>
            </div>
          ),
        },
        {
          key: 'edit-profile',
          label: t("dropdown_edit_profile"),
          onClick: () => navigate('/main/edit-profile'),
        },
        {
          key: '2',
          label: t("dropdown_logout"),
          onClick: () => {
            localStorage.clear();
            navigate('/');
          },
        },
      ]}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Header Section */}
      <Header style={{ 
        height: '90px', 
        background: '#001529', 
        padding: '0 30px', 
        color: '#fff', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <img 
          style={{ width: '200px', cursor: 'pointer' }} 
          src='https://daiichi.com/new_site/wp-content/uploads/2019/11/daiichi-logo-w.svg' 
          alt='Logo' 
          onClick={() => navigate('/main/customer')} 
        /> 

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* ✅ NOTIFICATION BELL (Sadece Supplier için) */}
          {user?.role === 'Supplier' && (
            <Dropdown 
              menu={notificationMenu}
              trigger={['click']}
              placement="bottomRight"
              overlayStyle={{ 
                width: '360px',
                maxHeight: '480px',
                overflowY: 'auto',
                boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                borderRadius: '8px',
              }}
              onOpenChange={(open) => {
                if (open) {
                  fetchNotifications();
                }
              }}
            >
              <div style={{ 
                cursor: 'pointer', 
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <Spin spinning={loading} size="small">
                  <Badge 
                    count={totalCount} 
                    overflowCount={99}
                    style={{ 
                      backgroundColor: notificationSummary?.critical > 0 ? '#ff4d4f' : '#faad14',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      fontWeight: '600',
                    }}
                    offset={[-4, 4]}
                  >
                    <BellOutlined style={{ 
                      fontSize: '20px', 
                      color: 'white',
                    }} />
                  </Badge>
                </Spin>
              </div>
            </Dropdown>
          )}

          {/* Dil değiştirici */}
          <LanguageSwitcher />

          {/* Kullanıcı Dropdown */}
          <Dropdown overlay={userMenu} trigger={['click']} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar size="large" icon={<UserOutlined />} style={{ marginRight: '10px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', color: '#fff' }}>
                <span style={{ fontWeight: 'bold' }}>{user.name}</span>
                <span style={{ fontSize: '12px', color: '#d9d9d9' }}>{user.email}</span>
              </div>
            </div>
          </Dropdown>
        </div>
      </Header>

      {/* Layout with Sidebar and Content */}
      <Layout>
        <Sider width={256} style={{ background: '#fff' }}>
          <Menu
            onClick={onClick}
            style={{ height: '100%' }}
            defaultSelectedKeys={['13']}
            mode="inline"
            items={items}
          />
        </Sider>

        {/* Ana İçerik Bölgesi */}
        <Content style={{ margin: '0 16px' }}>
          <Outlet />
        </Content>
      </Layout>

      {/* Footer Section */}
      <Footer style={{ textAlign: 'center' }}>Supplier Portal ©2024 Created by Daiichi</Footer>
    </Layout>
  );
};

export default MainLayout;
